import { localDb } from '@/data/local/database'
import type { PendingOperation, PullChange, PushResponse, SyncEntity, SyncEntityType } from '@/domain/types'
import { remoteApiRequest } from '@/data/api/remote-api'
import { normalizeSyncEntity } from '@/data/local/normalize-sync-entity'
import { getActiveIdentity } from '@/services/identity/local-identity'
import { syncCanvasSnapshots } from '@/services/sync/canvas-sync'
import { noteSemanticKey, notesAreSemanticDuplicates } from '@/services/sync/note-dedup'

const MAX_BATCH = 10
const MAX_RETRY_MS = 5 * 60_000
/** 版本冲突自动重推上限：超过仍冲突（极端并发）才降级为手动处理 */
const MAX_CONFLICT_RETRIES = 6
const REPLAY_REPAIR = '2.15.7-paged-entity-replay'
let running: Promise<void> | null = null
let retryTimer: ReturnType<typeof setTimeout> | null = null
let dirty = false

function entityTable(type: SyncEntityType) {
  switch (type) {
    case 'study_session': return localDb.studySessions
    case 'note': return localDb.notes
    case 'todo': return localDb.todos
    case 'recurring_todo': return localDb.recurringTodos
    case 'practice_record': return localDb.practiceRecords
    case 'inspiration_tag': return localDb.inspirationTags
    case 'user_settings': return localDb.userSettings
    case 'review_log': return localDb.reviewLogs
  }
}

async function putRemoteEntity(type: SyncEntityType, entity: Record<string, unknown>) {
  const normalized = normalizeSyncEntity(type, entity)
  switch (type) {
    case 'study_session': return localDb.studySessions.put(normalized as never)
    case 'note': return localDb.notes.put(normalized as never)
    case 'todo': return localDb.todos.put(normalized as never)
    case 'recurring_todo': return localDb.recurringTodos.put(normalized as never)
    case 'practice_record': return localDb.practiceRecords.put(normalized as never)
    case 'inspiration_tag': return localDb.inspirationTags.put(normalized as never)
    case 'user_settings': return localDb.userSettings.put(normalized as never)
    case 'review_log': return localDb.reviewLogs.put(normalized as never)
  }
}

async function updateLocalVersion(operation: PendingOperation, version: number) {
  const table = entityTable(operation.entityType)
  const entity = await table.get(operation.entityId) as SyncEntity | undefined
  if (entity) await putRemoteEntity(operation.entityType, { ...entity, version })
}

async function applyChanges(changes: PullChange[]) {
  for (const change of changes) {
    const pending = await localDb.pendingOperations
      .where('entityId').equals(change.entityId)
      .filter(item => item.entityType === change.entityType)
      .first()
    if (pending) continue
    const unresolved = await localDb.conflicts.where('entityId').equals(change.entityId)
      .filter(item => item.workspaceId === change.workspaceId && item.entityType === change.entityType && !item.resolvedAtMs).first()
    if (unresolved) continue
    if (change.entity) {
      await putRemoteEntity(change.entityType, change.entity)
    } else {
      const table = entityTable(change.entityType)
      const current = await table.get(change.entityId) as SyncEntity | undefined
      if (current) await putRemoteEntity(change.entityType, {
        ...current,
        version: change.version,
        updatedAtMs: change.changedAtMs,
        deletedAtMs: change.changedAtMs,
      })
    }
  }
}

async function pushPending(workspaceId: string) {
  const now = Date.now()
  const operations = await localDb.pendingOperations
    .where('workspaceId').equals(workspaceId)
    .filter(item => item.nextRetryAtMs <= now)
    .sortBy('createdAtMs')
  const batch = operations.slice(0, MAX_BATCH)
  if (!batch.length) return 0
  const response = await remoteApiRequest('sync/push', {
    method: 'POST',
    body: JSON.stringify({ workspaceId, operations: batch }),
  })
  if (response.status === 401) throw new Error('登录已失效，请在同步中心重新登录')
  if (!response.ok) throw new Error(`上传失败（HTTP ${response.status}）`)
  const result = await response.json() as PushResponse

  for (const accepted of result.accepted) {
    const operation = batch.find(item => item.operationId === accepted.operationId)
    if (operation) await updateLocalVersion(operation, accepted.version)
    await localDb.pendingOperations.delete(accepted.operationId)
  }
  for (const conflict of result.conflicts) {
    const operation = batch.find(item => item.operationId === conflict.operationId)
    if (!operation) continue
    const currentVersion = Number(conflict.current?.version)
    // 版本落后（多端并发或本地游标滞后）不等于编辑过期：用户最新一次保存
    // 必须无条件成为服务器内容。以服务器最新版本为基准、payload 刷新为本地
    // 实体当前内容后立即重推，最终写入的一定是本机最新编辑。
    if (Number.isFinite(currentVersion) && operation.retryCount < MAX_CONFLICT_RETRIES) {
      const table = entityTable(operation.entityType)
      const entity = operation.action === 'delete'
        ? undefined
        : (await table.get(operation.entityId)) as SyncEntity | undefined
      await localDb.pendingOperations.update(operation.operationId, {
        // 实体已被本地删除时重推删除动作，避免复活已删卡片
        action: entity ? 'update' : 'delete',
        ...(entity ? { payload: { ...entity, updatedAtMs: Date.now() } } : {}),
        baseVersion: currentVersion,
        retryCount: operation.retryCount + 1,
        nextRetryAtMs: Date.now(),
      })
      continue
    }
    await localDb.conflicts.put({
      id: `conflict_${crypto.randomUUID()}`,
      workspaceId,
      entityType: operation.entityType,
      entityId: operation.entityId,
      operationId: operation.operationId,
      localPayload: operation.payload,
      serverEntity: conflict.current || {},
      detectedAtMs: Date.now(),
      resolvedAtMs: null,
      resolution: null,
    })
    await localDb.pendingOperations.delete(operation.operationId)
  }
  for (const rejected of result.rejected) {
    const operation = batch.find(item => item.operationId === rejected.operationId)
    if (!operation) continue
    await localDb.pendingOperations.update(operation.operationId, {
      retryCount: operation.retryCount + 1,
      nextRetryAtMs: Date.now() + Math.min(MAX_RETRY_MS, 2 ** operation.retryCount * 2000),
    })
  }
  return batch.length
}

async function pullChanges(workspaceId: string) {
  const state = await localDb.syncState.get(workspaceId)
  let cursor = state?.lastSequence || 0
  let more = true
  while (more) {
    const response = await remoteApiRequest(`sync/pull?workspaceId=${encodeURIComponent(workspaceId)}&cursor=${cursor}`, {
      signal: AbortSignal.timeout(180_000),
    })
    if (response.status === 401) throw new Error('登录已失效，请在同步中心重新登录')
    if (!response.ok) throw new Error(`下载失败（HTTP ${response.status}）`)
    const result = await response.json() as {
      changes: PullChange[]
      nextCursor: number
      hasMore: boolean
      serverTimeMs: number
    }
    if (!Array.isArray(result.changes) || !Number.isSafeInteger(result.nextCursor)
      || result.nextCursor < cursor || (result.hasMore && result.nextCursor === cursor)) {
      throw new Error('同步分页无效，已保留上次进度')
    }
    // Cursor and entity writes commit together. A timeout/crash resumes the last
    // successful page, never a cursor whose cards have not reached IndexedDB.
    await localDb.transaction('rw', [localDb.studySessions, localDb.notes, localDb.todos,
      localDb.recurringTodos, localDb.practiceRecords, localDb.inspirationTags,
      localDb.userSettings, localDb.reviewLogs, localDb.pendingOperations, localDb.conflicts, localDb.syncState], async () => {
      await applyChanges(result.changes)
      await localDb.syncState.update(workspaceId, {
        lastSequence: result.nextCursor,
        serverTimeOffsetMs: result.serverTimeMs - Date.now(),
      })
    })
    cursor = result.nextCursor
    more = result.hasMore
    if (result.changes.length) window.dispatchEvent(new CustomEvent('study-tool:sync-page-applied', {
      detail: { workspaceId, entityTypes: [...new Set(result.changes.map(change => change.entityType))] },
    }))
  }
}

export async function hydrateCloudSnapshot(force = false, onlyTypes?: SyncEntityType[]) {
  const identity = await getActiveIdentity()
  if (!identity.authenticated || !navigator.onLine) return false
  const [pendingOperations, unresolvedConflicts] = await Promise.all([
    localDb.pendingOperations.where('workspaceId').equals(identity.workspaceId).toArray(),
    localDb.conflicts
      .where('workspaceId')
      .equals(identity.workspaceId)
      .filter(item => !item.resolvedAtMs)
      .toArray(),
  ])

  // A snapshot must never overwrite edits that have not reached the server yet.
  // Older versions avoided that risk by skipping hydration entirely whenever one
  // pending operation/conflict existed. That left partially populated browsers
  // stuck at a high sync cursor forever (cards might exist while focus/practice
  // rows were missing), so every statistic rendered as zero. Protect only the
  // affected entities and repair the rest of the workspace from the snapshot.
  const protectedIds = new Map<SyncEntityType, Set<string>>()
  const protect = (type: SyncEntityType, id: string) => {
    const ids = protectedIds.get(type) || new Set<string>()
    ids.add(id)
    protectedIds.set(type, ids)
  }
  for (const operation of pendingOperations) protect(operation.entityType, operation.entityId)
  for (const conflict of unresolvedConflicts) protect(conflict.entityType, conflict.entityId)

  // 启动校准是"全量下载 + 清空重建"，数据量大（含 base64 图片）在移动网络下
  // 会卡在连接中。本地已有数据时跳过全量校准，交给增量 pull 保持同步；
  // 只有本地空库（首登/换设备/清库）或手动强制校准（顶部刷新）才拉全量。
  if (!force) {
    const localCount = await localDb.notes.where('workspaceId').equals(identity.workspaceId).count()
      + await localDb.studySessions.where('workspaceId').equals(identity.workspaceId).count()
      + await localDb.todos.where('workspaceId').equals(identity.workspaceId).count()
    if (localCount > 0) return false
  }

  const typeQuery = onlyTypes?.length ? `&types=${encodeURIComponent(onlyTypes.join(','))}` : ''
  const response = await remoteApiRequest(
    `sync/bootstrap?workspaceId=${encodeURIComponent(identity.workspaceId)}${typeQuery}`,
  )
  if (response.status === 401) {
    // Session expired (e.g. server DB reset or 90-day TTL) — force re-login
    // instead of leaving the app in a half-authenticated state.
    window.dispatchEvent(new CustomEvent('study-tool:session-expired'))
    return false
  }
  if (!response.ok) throw new Error(`完整数据校准失败（HTTP ${response.status}）`)
  const result = await response.json() as {
    entities: Array<{ entityType: SyncEntityType; entity: Record<string, unknown> }>
    cursor: number
    serverTimeMs: number
  }

  // Data protection: if the server snapshot is empty but we have local data,
  // the server likely lost data (e.g. failed pushes) — do NOT wipe local.
  if (!onlyTypes?.length && result.entities.length === 0) {
    const localCount = await localDb.notes.where('workspaceId').equals(identity.workspaceId).count()
      + await localDb.studySessions.where('workspaceId').equals(identity.workspaceId).count()
      + await localDb.todos.where('workspaceId').equals(identity.workspaceId).count()
    if (localCount > 0) return false
  }
  const grouped: Record<SyncEntityType, Record<string, unknown>[]> = {
    study_session: [],
    note: [],
    todo: [],
    recurring_todo: [],
    practice_record: [],
    inspiration_tag: [],
    user_settings: [],
    review_log: [],
  }
  for (const item of result.entities) {
    if (item.entityType in grouped) grouped[item.entityType].push(normalizeSyncEntity(item.entityType, item.entity))
  }
  const snapshotTypes = new Set<SyncEntityType>(onlyTypes?.length ? onlyTypes : Object.keys(grouped) as SyncEntityType[])

  await localDb.transaction('rw', [
    localDb.studySessions,
    localDb.notes,
    localDb.todos,
    localDb.recurringTodos,
    localDb.practiceRecords,
    localDb.inspirationTags,
    localDb.userSettings,
    localDb.reviewLogs,
    localDb.syncState,
  ], async () => {
    const remoteRows = (type: SyncEntityType) => {
      const ids = protectedIds.get(type) || new Set<string>()
      return grouped[type].filter(item => !ids.has(String(item.id)))
    }

    const clears: Promise<unknown>[] = []
    if (snapshotTypes.has('study_session')) clears.push(localDb.studySessions.where('workspaceId').equals(identity.workspaceId)
      .filter(item => !(protectedIds.get('study_session') || new Set()).has(item.id)).delete())
    if (snapshotTypes.has('note')) clears.push(localDb.notes.where('workspaceId').equals(identity.workspaceId)
      .filter(item => !(protectedIds.get('note') || new Set()).has(item.id)).delete())
    if (snapshotTypes.has('todo')) clears.push(localDb.todos.where('workspaceId').equals(identity.workspaceId)
      .filter(item => !(protectedIds.get('todo') || new Set()).has(item.id)).delete())
    if (snapshotTypes.has('recurring_todo')) clears.push(localDb.recurringTodos.where('workspaceId').equals(identity.workspaceId)
      .filter(item => !(protectedIds.get('recurring_todo') || new Set()).has(item.id)).delete())
    if (snapshotTypes.has('practice_record')) clears.push(localDb.practiceRecords.where('workspaceId').equals(identity.workspaceId)
      .filter(item => !(protectedIds.get('practice_record') || new Set()).has(item.id)).delete())
    if (snapshotTypes.has('inspiration_tag')) clears.push(localDb.inspirationTags.where('workspaceId').equals(identity.workspaceId)
      .filter(item => !(protectedIds.get('inspiration_tag') || new Set()).has(item.id)).delete())
    if (snapshotTypes.has('user_settings')) clears.push(localDb.userSettings.where('workspaceId').equals(identity.workspaceId)
      .filter(item => !(protectedIds.get('user_settings') || new Set()).has(item.id)).delete())
    if (snapshotTypes.has('review_log')) clears.push(localDb.reviewLogs.where('workspaceId').equals(identity.workspaceId)
      .filter(item => !(protectedIds.get('review_log') || new Set()).has(item.id)).delete())
    await Promise.all(clears)

    const inserts: Promise<unknown>[] = []
    if (snapshotTypes.has('study_session')) inserts.push(localDb.studySessions.bulkPut(remoteRows('study_session') as never[]))
    if (snapshotTypes.has('note')) inserts.push(localDb.notes.bulkPut(remoteRows('note') as never[]))
    if (snapshotTypes.has('todo')) inserts.push(localDb.todos.bulkPut(remoteRows('todo') as never[]))
    if (snapshotTypes.has('recurring_todo')) inserts.push(localDb.recurringTodos.bulkPut(remoteRows('recurring_todo') as never[]))
    if (snapshotTypes.has('practice_record')) inserts.push(localDb.practiceRecords.bulkPut(remoteRows('practice_record') as never[]))
    if (snapshotTypes.has('inspiration_tag')) inserts.push(localDb.inspirationTags.bulkPut(remoteRows('inspiration_tag') as never[]))
    if (snapshotTypes.has('user_settings')) inserts.push(localDb.userSettings.bulkPut(remoteRows('user_settings') as never[]))
    if (snapshotTypes.has('review_log')) inserts.push(localDb.reviewLogs.bulkPut(remoteRows('review_log') as never[]))
    await Promise.all(inserts)
    await localDb.syncState.update(identity.workspaceId, {
      // A statistics-only snapshot has NOT downloaded note/tag changes. Advancing
      // the global cursor here permanently skipped cards on partially synced clients.
      ...(snapshotTypes.size === 8 ? {
        lastSequence: result.cursor, lastSuccessAtMs: Date.now(), status: 'idle' as const, errorMessage: null,
      } : {}),
      serverTimeOffsetMs: result.serverTimeMs - Date.now(),
    })
  })
  window.dispatchEvent(new CustomEvent('study-tool:snapshot-hydrated'))
  return true
}

/**
 * v2.7.8 one-time repair for browsers whose local entity tables became partial
 * while their sync cursor still pointed at the newest server change.
 */
export async function repairCloudSnapshotOnce(repairId = REPLAY_REPAIR) {
  const identity = await getActiveIdentity()
  if (!identity.authenticated || !navigator.onLine) return false
  const key = `cloudSnapshotRepair:${repairId}:${identity.workspaceId}`
  if ((await localDb.appMetadata.get(key))?.value === true) return false
  await beginPagedReplay(identity.workspaceId, repairId)
  await requestSync()
  return (await localDb.appMetadata.get(key))?.value === true
}

async function beginPagedReplay(workspaceId: string, repairId = REPLAY_REPAIR) {
  const key = `cloudSnapshotRepair:${repairId}:${workspaceId}`
  await localDb.transaction('rw', localDb.appMetadata, localDb.syncState, async () => {
    if (await localDb.appMetadata.get(key)) return
    await localDb.syncState.update(workspaceId, { lastSequence: 0 })
    await localDb.appMetadata.put({ key, value: 'in-progress', updatedAtMs: Date.now() })
  })
}

async function finishPagedReplay(workspaceId: string) {
  const rows = await localDb.appMetadata.filter(row => row.key.startsWith('cloudSnapshotRepair:')
    && row.key.endsWith(`:${workspaceId}`) && row.value === 'in-progress').toArray()
  await localDb.appMetadata.bulkPut(rows.map(row => ({ ...row, value: true, updatedAtMs: Date.now() })))
}

/**
 * Restore the small, user-visible statistics tables before downloading the much
 * larger note snapshot (which can contain base64 question and handwriting
 * images). This prevents a fresh browser/device from sitting on the login page
 * or briefly presenting all-zero totals while the card library is transferred.
 */
export async function repairCriticalUserStatsOnce(repairId = '2.8.0-critical-user-stats') {
  const identity = await getActiveIdentity()
  if (!identity.authenticated || !navigator.onLine) return false
  const key = `cloudSnapshotRepair:${repairId}:${identity.workspaceId}`
  if ((await localDb.appMetadata.get(key))?.value === true) return false
  if (!await hydrateCloudSnapshot(true, ['study_session', 'practice_record'])) return false
  await localDb.appMetadata.put({ key, value: true, updatedAtMs: Date.now() })
  return true
}

/**
 * 把历史遗留的未解决冲突自动按"本地最新为准"重新排队上传。
 * 旧版本把版本冲突留成待手动处理的长列表，且"采用云端"会直接覆盖掉
 * 本机编辑。现按用户语义收敛：手动保存过的本机内容无条件成为服务器内容，
 * 服务器版本仅用作下一次 push 的 baseVersion。
 */
async function queueUnresolvedConflicts(identity: { workspaceId: string; deviceId: string }) {
  const stale = await localDb.conflicts
    .where('workspaceId').equals(identity.workspaceId)
    .filter(item => !item.resolvedAtMs)
    .toArray()
  if (!stale.length) return
  const now = Date.now()
  for (const conflict of stale) {
    const table = entityTable(conflict.entityType)
    const entity = (await table.get(conflict.entityId)) as SyncEntity | undefined
    const payload = entity
      ? { ...entity, updatedAtMs: now }
      : conflict.localPayload
    if (payload && Object.keys(payload).length) {
      await localDb.pendingOperations.add({
        operationId: `op_${crypto.randomUUID()}`,
        deviceId: identity.deviceId,
        workspaceId: identity.workspaceId,
        entityType: conflict.entityType,
        entityId: conflict.entityId,
        action: entity ? 'update' : 'delete',
        baseVersion: Number(conflict.serverEntity?.version) || null,
        clientUpdatedAtMs: now,
        payload,
        retryCount: 0,
        nextRetryAtMs: now,
        createdAtMs: now,
      })
    }
    await localDb.conflicts.update(conflict.id, { resolvedAtMs: now, resolution: 'local_latest' })
  }
}

async function performSync() {
  const identity = await getActiveIdentity()
  if (!identity.authenticated) return
  if (!navigator.onLine) {
    await localDb.syncState.update(identity.workspaceId, { status: 'offline' })
    return
  }
  await localDb.syncState.update(identity.workspaceId, {
    status: 'syncing',
    lastAttemptAtMs: Date.now(),
    errorMessage: null,
  })
  try {
    await beginPagedReplay(identity.workspaceId)
    await queueUnresolvedConflicts(identity)
    // Uploading a large handwriting queue must not starve incoming cards.
    await pullChanges(identity.workspaceId)
    await finishPagedReplay(identity.workspaceId)
    while (await pushPending(identity.workspaceId)) {
      // Drain bounded batches so each Worker request stays below mobile timeouts.
    }
    await pullChanges(identity.workspaceId)
    // 画布树是独立快照，不在普通实体 change_log 中。每轮同步至少冲刷所有
    // pending 画布，确保关闭画布后仍能把离线层级/排序传到服务器。
    await syncCanvasSnapshots({ pullRemote: true })
    const conflicts = await localDb.conflicts.where('workspaceId').equals(identity.workspaceId).filter(item => !item.resolvedAtMs).count()
    await localDb.syncState.update(identity.workspaceId, {
      status: conflicts ? 'conflict' : 'idle',
      lastSuccessAtMs: Date.now(),
      errorMessage: null,
    })
    window.dispatchEvent(new CustomEvent('study-tool:synced'))
  } catch (error) {
    const message = error instanceof Error ? error.message : '同步失败'
    await localDb.syncState.update(identity.workspaceId, { status: navigator.onLine ? 'error' : 'offline', errorMessage: message })
    scheduleRetry()
  }
}

function scheduleRetry() {
  if (retryTimer) return
  retryTimer = setTimeout(() => {
    retryTimer = null
    void requestSync()
  }, 15_000)
}

export function requestSync() {
  if (running) {
    // A sync is in flight — remember that new changes arrived and re-run
    // immediately after the current round finishes, so saves/reviews are
    // never left waiting for the next event.
    dirty = true
    return running
  }
  running = performSync().finally(() => {
    running = null
    if (dirty) {
      dirty = false
      void requestSync()
    }
  })
  return running
}

/** 用户主动同步：普通实体完成后，再拉取全部画布模块的权威快照。 */
export async function requestFullSync() {
  await requestSync()
  await syncCanvasSnapshots({ pullRemote: true })
}

export function startSyncEngine() {
  const listener = () => void requestSync()
  window.addEventListener('study-tool:sync-request', listener)
  void requestSync()
  return () => {
    window.removeEventListener('study-tool:sync-request', listener)
    if (retryTimer) clearTimeout(retryTimer)
  }
}

export async function resolveSyncConflict(id: string, resolution: 'cloud' | 'local_copy') {
  const conflict = await localDb.conflicts.get(id)
  if (!conflict || conflict.resolvedAtMs) return
  const identity = await getActiveIdentity()
  const now = Date.now()
  if (resolution === 'cloud') {
    if (Object.keys(conflict.serverEntity).length) {
      await putRemoteEntity(conflict.entityType, conflict.serverEntity)
    } else {
      const table = entityTable(conflict.entityType)
      const current = await table.get(conflict.entityId) as SyncEntity | undefined
      if (current) await putRemoteEntity(conflict.entityType, { ...current, deletedAtMs: now, updatedAtMs: now })
    }
  } else {
    if (conflict.entityType === 'user_settings') {
      const current = await localDb.userSettings.get(identity.userId)
      const serverVersion = Number(conflict.serverEntity.version)
      if (current && Number.isFinite(serverVersion)) {
        await localDb.pendingOperations.add({
          operationId: `op_${crypto.randomUUID()}`,
          deviceId: identity.deviceId,
          workspaceId: identity.workspaceId,
          entityType: 'user_settings',
          entityId: identity.userId,
          action: 'update',
          baseVersion: serverVersion,
          clientUpdatedAtMs: now,
          payload: { ...current, id: identity.userId, updatedAtMs: now },
          retryCount: 0,
          nextRetryAtMs: now,
          createdAtMs: now,
        })
        await localDb.conflicts.update(id, { resolvedAtMs: now, resolution })
        window.dispatchEvent(new CustomEvent('study-tool:sync-request'))
        return
      }
    }
    if (conflict.entityType === 'note') {
      // Repeatedly resolving equivalent cross-device edits used to create a new
      // copy_* note for every conflict. Treat identical card content as one
      // logical card, and reuse an existing copy when a previous resolution
      // already preserved the local payload.
      if (notesAreSemanticDuplicates(conflict.localPayload, conflict.serverEntity)) {
        if (Object.keys(conflict.serverEntity).length) await putRemoteEntity('note', conflict.serverEntity)
        await localDb.conflicts.update(id, { resolvedAtMs: now, resolution })
        window.dispatchEvent(new CustomEvent('study-tool:sync-request'))
        return
      }
      const localKey = noteSemanticKey(conflict.localPayload)
      if (localKey) {
        const existing = (await localDb.notes.where('workspaceId').equals(identity.workspaceId).toArray())
          .find(note => note.id !== conflict.entityId && !note.deletedAtMs && noteSemanticKey(note) === localKey)
        if (existing) {
          await localDb.conflicts.update(id, { resolvedAtMs: now, resolution })
          window.dispatchEvent(new CustomEvent('study-tool:sync-request'))
          return
        }
      }
    }
    const entityId = `copy_${crypto.randomUUID()}`
    const copy = {
      ...conflict.localPayload,
      id: entityId,
      workspaceId: identity.workspaceId,
      ownerUserId: identity.userId,
      version: 0,
      createdAtMs: now,
      updatedAtMs: now,
      deletedAtMs: null,
    }
    await putRemoteEntity(conflict.entityType, copy)
    await localDb.pendingOperations.add({
      operationId: `op_${crypto.randomUUID()}`,
      deviceId: identity.deviceId,
      workspaceId: identity.workspaceId,
      entityType: conflict.entityType,
      entityId,
      action: 'create',
      baseVersion: null,
      clientUpdatedAtMs: now,
      payload: copy,
      retryCount: 0,
      nextRetryAtMs: now,
      createdAtMs: now,
    })
  }
  await localDb.conflicts.update(id, { resolvedAtMs: now, resolution })
  if (resolution === 'cloud') {
    // “采用云端”过去只处理普通实体，画布层级仍停留在旧本地缓存。
    // 同步拉取画布快照，使该操作对用户看到的整套资料真正生效。
    await syncCanvasSnapshots({ pullRemote: true })
  }
  window.dispatchEvent(new CustomEvent('study-tool:sync-request'))
}
